import { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown, Loader2 } from 'lucide-react';
import { useLanguage } from './LanguageContext';

export function LanguagePicker() {
  const { currentLanguage, availableLanguages, switchLanguage, isChanging } =
    useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (availableLanguages.length <= 1) return null;

  const currentLang = availableLanguages.find(
    (l) => l.cultureName === currentLanguage
  );

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-2 text-sm text-neutral-700 hover:text-primary-600 rounded-md transition-colors min-h-[44px]"
        aria-label="Change language"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={isChanging}
      >
        {isChanging ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Globe className="w-4 h-4" />
        )}
        <span className="hidden sm:inline">
          {currentLang?.displayName || currentLanguage}
        </span>
        <ChevronDown className="w-3 h-3" />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          aria-label="Select language"
          className="absolute right-0 top-full mt-1 w-56 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 py-1 max-h-80 overflow-y-auto"
        >
          {availableLanguages.map((lang) => {
            const isActive = lang.cultureName === currentLanguage;
            return (
              <li key={lang.cultureName} role="option" aria-selected={isActive}>
                <button
                  onClick={async () => {
                    await switchLanguage(lang.cultureName);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors min-h-[44px] ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-neutral-700 hover:bg-neutral-50'
                  }`}
                  disabled={isChanging}
                >
                  <span className="flex-1 text-left">{lang.displayName}</span>
                  <span className="text-xs text-neutral-400">
                    {lang.cultureName}
                  </span>
                  {isActive && (
                    <Check className="w-4 h-4 text-primary-600 flex-shrink-0" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
